import { useNavigate } from "react-router-dom";
import { useRef, useState } from "react";
import { useRepositoriesContext } from "../../../../contexts/RepositoriesContext";
import { useProfileContext } from "../../../../contexts/ProfileContext";
import { Design, DesignLogo } from "../../../../core/models/entities/Design";
import { CreateDesignDto } from "../../../../core/models/dtos/CreateDesignDto";
import { InitiateApproval } from "../../logo-list/hooks/useLogoActionsHandler";
import { useOrderActionHandler } from "../../order-create/hooks/useOrderActionsHandler";
import { Verdict } from "../../../components/ApprovalVoting/approval-voting";
import { withToasts } from "../../../components/Toast/Toast";
import { enviroment } from "../../../../enviroment";
import {
  DESIGNS_SKIPPED_NOT_APPROVED_KEY,
  isDesignOrderable,
  splitOrderableDesigns,
} from "../../../../utils/functions/orderability";
import { Role } from "../../../../core/models/entities/Role";
import { useApiClient } from "../../../../data/clients/ApiClient";
import { mapToDesign } from "../../../../data/models/mappers/mapDesign";
import { useCartHidden } from "../../../../hooks/useCartHidden";
import { toast } from "react-toastify";
import { useTranslation } from "react-i18next";

interface DesignActionHandler {
  isLoading: boolean;
  handleCopyDesign: (design: Design) => Promise<Design>;
  handleCreateDesign: (design: CreateDesignDto) => Promise<Design>;
  handleGoToCreateDesign: (id?: number) => void;
  handleGoToDesignList: () => void;
  handleUpdateDesign: (design: Design) => Promise<Design>;
  handleDeleteDesign: (design: Design) => Promise<void>;
  handleGoToUpdateDesign: (design: Design, addLogoId?: number, options?: { justCloned?: boolean }) => void;
  handleAddToCart: (design: Design, quantity: number) => Promise<void>;
  handleAddItemsToCart: (designs: Design[], quantity: number) => Promise<void>;
  handleInitiateApproval: (
    design: Design,
    approval: InitiateApproval
  ) => Promise<void>;
  handlePlaceVerdict: (design: Design, verdict: Verdict) => Promise<Design>;
  handleDeleteLogo: (design: Design) => Promise<void>;
  handleUpdateThenCommit: (design: Design) => Promise<Design>;
  handleCommit: (design: Design) => Promise<Design>;
  handleApprovalSkip: (design: Design) => Promise<Design>;
  handleSendApproval: (design: Design, email: string) => Promise<void>;
  handleSendApprovalWithEndCustomerPending: (
    design: Design,
    email: string
  ) => Promise<void>;
  handleChangeStyle(
    orgId: number,
    designId: number,
    styleId: number
  ): Promise<Design>;
  handleChangeTitle(
    orgId: number,
    designId: number,
    title: string
  ): Promise<Design>;
  handleSkipAddToCart(design: Design, quantity: number): Promise<Design>;
  handleSettleAndAddToCart: (design: Design, quantity: number) => Promise<void>;
  handleRejectAndDelete: (design: Design, note?: string) => Promise<void>;
  handleRejectAndEdit: (design: Design, note: string) => Promise<Design>;
}

export function useDesignActionHandler(): DesignActionHandler {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const [isLoading, setIsLoading] = useState(false);
  const { profile } = useProfileContext();
  const role = profile?.userOrganizationInformations.role;

  const { designRepository } = useRepositoriesContext();
  const { handleDesignAddOrderLine, handleDesignAddOrderLines } =
    useOrderActionHandler();
  const apiClient = useApiClient();
  const cartHidden = useCartHidden();
  // handleSettleAndAddToCart: the designs whose settle-and-order chain is
  // running (approval-start -> NAV commit -> GetDesign -> cart line, up to
  // the API's 15 s NAV box). A second click on the same row while it runs
  // is ignored - never two approval-starts (the second would hit a design
  // that is already done and 400) and never two cart lines.
  const settlingDesignIds = useRef<Set<number>>(new Set());

  function handleCopyDesign(design: Design) {
    async function transaction() {
      return await designRepository.copyDesign(design.customerId, design.id);
    }

    return withToasts(
      transaction,
      t("designListStyle.copyingDesign"),
      t("designListStyle.designCopied"),
      t("designListStyle.copyDesignFailed"),
      setIsLoading
    );
  }

  function handleCreateDesign(design: CreateDesignDto): Promise<Design> {
    async function transaction() {
      return await designRepository.createDesign({ ...design });
    }

    return withToasts(
      transaction,
      t("designListStyle.creatingDesign"),
      t("designListStyle.designCreated"),
      t("designListStyle.createDesignFailed"),
      setIsLoading
    );
  }

  function handleChangeStyle(
    orgId: number,
    designId: number,
    styleId: number
  ): Promise<Design> {
    async function transaction() {
      return await designRepository.changeStyle(orgId, designId, styleId);
    }

    return withToasts(
      transaction,
      t("designListStyle.updatingDesignStyle"),
      t("designListStyle.designStyleUpdated"),
      t("designListStyle.updateDesignStyleFailed"),
      setIsLoading
    );
  }

  function handleChangeTitle(orgId: number, designId: number, title: string) {
    async function transaction() {
      return await designRepository.changeTitle(orgId, designId, title);
    }

    return withToasts(
      transaction,
      t("designListStyle.updatingDesignTitle"),
      t("designListStyle.designTitleUpdated"),
      t("designListStyle.updateDesignTitleFailed"),
      setIsLoading
    );
  }

  function handleAddToCart(design: Design, quantity: number): Promise<void> {
    if (cartHidden) {
      toast.error(t("designListStyle.cartNotAvailable"));
      return Promise.resolve();
    }
    // Oversize sheet guard (flag REACT_APP_OVERSIZE_SHEET_GUARD_ENABLED): a
    // refused design was NOT put in the cart (its dialog is open), so no
    // "Added to cart" for it. Flag off = refused 0 = today's toast.
    let refusedByGuard = false;
    async function transaction() {
      let orgId: number;

      if (profile?.userOrganizationInformations.organizationId !== undefined) {
        orgId = profile.userOrganizationInformations.organizationId;
      } else if (role === 1) {
        orgId = design.vendor?.id ?? 0;
      } else if (role === 0) {
        orgId = design.customer?.id ?? 0;
      } else if (role === 2) {
        orgId = 0;
      } else {
        orgId = 0;
      }

      const designDto = await apiClient.getDesign(orgId, design.id);
      const fullDesign = mapToDesign(designDto);
      // Flag designOrderabilityGuardEnabled (#724425): the row hides its
      // button for a non-orderable design, but the popup's "Add to order"
      // (status done) and a stale row can still land here - same rule, on
      // the freshly fetched design (0 extra calls): a design that is not
      // orderable is NOT put in the cart; the skip toast is the answer.
      if (
        enviroment.designOrderabilityGuardEnabled &&
        !isDesignOrderable(fullDesign)
      ) {
        toast.error(t(DESIGNS_SKIPPED_NOT_APPROVED_KEY, { count: 1 }));
        refusedByGuard = true;
        return;
      }
      const deletedDesignLogos = fullDesign.designLogos.filter(
        (dl) => dl.logo.isDeleted,
      );
      const filteredDesign: Design = {
        ...fullDesign,
        designLogos: fullDesign.designLogos.filter((dl) => !dl.logo.isDeleted),
      };
      const outcome = handleDesignAddOrderLine(
        filteredDesign,
        quantity,
        undefined,
        deletedDesignLogos.length > 0 ? deletedDesignLogos : undefined,
      );
      refusedByGuard = !!outcome && outcome.refused > 0 && outcome.added === 0;
    }

    return withToasts(
      transaction,
      t("designListStyle.addingToCart"),
      t("designListStyle.addedToCart"),
      t("designListStyle.addToCartFailed"),
      setIsLoading,
      { showSuccess: () => !refusedByGuard }
    );
  }

  function handleAddItemsToCart(
    designs: Design[],
    quantity: number
  ): Promise<void> {
    if (cartHidden) {
      toast.error(t("designListStyle.cartNotAvailable"));
      return Promise.resolve();
    }
    // Flag designOrderabilityGuardEnabled (#724425): bulk "Add to Order (n)"
    // skips every design that may not be ordered (rule §1) and says how many
    // («n designs skipped – approval pending»); the orderable rest goes in
    // as today. All skipped = nothing added, no success toast. Flag off =
    // the selection untouched.
    if (enviroment.designOrderabilityGuardEnabled) {
      const split = splitOrderableDesigns(designs);
      if (split.skipped.length > 0) {
        toast.error(
          t(DESIGNS_SKIPPED_NOT_APPROVED_KEY, { count: split.skipped.length }),
        );
      }
      if (split.orderable.length === 0) return Promise.resolve();
      designs = split.orderable;
    }
    const excludedDeletedDesignLogos = new Map<number, DesignLogo[]>();
    const filteredDesigns = designs.map((design) => {
      const deletedLogos = design.designLogos.filter((dl) => dl.logo.isDeleted);
      if (deletedLogos.length > 0) {
        excludedDeletedDesignLogos.set(design.id, deletedLogos);
      }
      return {
        ...design,
        designLogos: design.designLogos.filter((dl) => !dl.logo.isDeleted),
      };
    });
    handleDesignAddOrderLines(
      filteredDesigns,
      quantity,
      undefined,
      excludedDeletedDesignLogos.size > 0
        ? excludedDeletedDesignLogos
        : undefined,
    );
    return Promise.resolve();
  }

  function handleUpdateDesign(design: Design) {
    async function transaction() {
      return await designRepository.updateDesign(design);
    }

    return withToasts(
      transaction,
      t("designListStyle.updatingDesign"),
      t("designListStyle.designUpdated"),
      t("designListStyle.updateDesignFailed"),
      setIsLoading
    );
  }

  function handleDeleteDesign(design: Design) {
    async function transaction() {
      return await designRepository.deleteDesign(design.customerId, design.id);
    }

    return withToasts(
      transaction,
      t("designListStyle.deletingDesign"),
      t("designListStyle.designDeleted"),
      t("designListStyle.deleteDesignFailed"),
      setIsLoading
    );
  }

  function handleGoToCreateDesign(id?: number) {
    navigate("/design/create" + (id ? `/${id}` : ""));
  }

  function handleGoToDesignList() {
    navigate("/design");
  }

  function handleGoToUpdateDesign(design: Design, addLogoId?: number, options?: { justCloned?: boolean }) {
    const query = addLogoId ? `&addLogoId=${addLogoId}` : "";
    // justCloned lets the editor show a neutral "preparing" state instead of the
    // error flash while a fresh clone's first GetDesign heals (design-editor2.screen).
    navigate(`/design/${design.id}/editor?orgId=${design.customer.id}${query}`,
      options?.justCloned ? { state: { justCloned: true } } : undefined);
  }

  function handleUpdateThenCommit(design: Design) {
    async function transaction() {
      await designRepository.updateDesign(design);
      return await designRepository.commit(design);
    }

    return withToasts(
      transaction,
      t("designListStyle.updatingAndCommittingDesign"),
      t("designListStyle.designUpdatedAndCommitted"),
      t("designListStyle.updateAndCommitDesignFailed"),
      setIsLoading
    );
  }

  function handleCommit(design: Design) {
    async function transaction() {
      return await designRepository.commit(design);
    }

    return withToasts(
      transaction,
      t("designListStyle.committingDesign"),
      t("designListStyle.designCommitted"),
      t("designListStyle.commitDesignFailed"),
      setIsLoading
    );
  }

  function handleDeleteLogo(design: Design) {
    async function transaction() {
      return await designRepository.deleteDesign(design.customer.id, design.id);
    }

    return withToasts(
      transaction,
      t("designListStyle.deletingDesign"),
      t("designListStyle.designDeleted"),
      t("designListStyle.deleteDesignLogoFailed"),
      setIsLoading
    );
  }

  function handlePlaceVerdict(design: Design, verdict: Verdict) {
    async function transaction() {
      return await designRepository.placeVerdict(design, verdict);
    }

    return withToasts(
      transaction,
      t("designListStyle.updatingApproval"),
      t("designListStyle.verdictSuccessful"),
      t("designListStyle.approvalFailed"),
      setIsLoading
    );
  }

  function handleInitiateApproval(design: Design, approval: InitiateApproval) {
    async function transaction() {
      const approvedDesign = await designRepository.initiateApproval(
        design,
        approval
      );
      // return await designRepository.placeVerdict(approvedDesign, { approve: true })
    }

    return withToasts(
      transaction,
      t("designListStyle.updatingApproval"),
      t("designListStyle.approvalSuccessful"),
      t("designListStyle.approvalFailed"),
      setIsLoading
    );
  }

  function handleApprovalSkip(design: Design): Promise<Design> {
    async function transaction() {
      // Vendors and Admins can truly skip approval, Customers send for approval
      const shouldSkip = role === Role.Admin || role === Role.Vendor;

      return await designRepository.initiateApproval(design, {
        skip: shouldSkip,
        email: undefined,
      });
    }

    const loadingMessage =
      role === Role.Admin || role === Role.Vendor
        ? t("designListStyle.skippingApproval")
        : t("designListStyle.creatingDesign");
    const successMessage =
      role === Role.Admin || role === Role.Vendor
        ? t("designListStyle.approvalSkipped")
        : t("designListStyle.designCreatedSuccessfully");
    const errorMessage =
      role === Role.Admin || role === Role.Vendor
        ? t("designListStyle.approvalSkipFailed")
        : t("designListStyle.sendForApprovalFailed");

    return withToasts(
      transaction,
      loadingMessage,
      successMessage,
      errorMessage,
      setIsLoading
    );
  }

  function handleSendApproval(design: Design, email: string) {
    async function transaction() {
      return await designRepository.sendApproval(design, email);
    }

    return withToasts(
      transaction,
      t("designListStyle.sendingEmail"),
      t("designListStyle.emailSendSuccessful"),
      t("designListStyle.emailSendFailed"),
      setIsLoading
    );
  }

  function handleSendApprovalWithEndCustomerPending(
    design: Design,
    email: string
  ) {
    async function transaction() {
      // Use conditional approval that preserves existing approval statuses
      return await designRepository.sendApprovalConditional(design, email);
    }

    return withToasts(
      transaction,
      t("designListStyle.sendingApproval"),
      t("designListStyle.approvalSendSuccessful"),
      t("designListStyle.approvalSendFailed"),
      setIsLoading
    );
  }

  function handleRejectAndDelete(design: Design, note?: string) {
    async function transaction() {
      const placedVerdict = await designRepository.placeVerdict(design, {
        approve: false,
        rejectionNote: note ?? "",
      });
      return await designRepository.deleteDesign(
        placedVerdict.customerId,
        design.id
      );
    }

    return withToasts(
      transaction,
      t("designListStyle.deletingDesign"),
      t("designListStyle.deletionSuccessful"),
      t("designListStyle.deletionFailed"),
      setIsLoading
    );
  }

  // "Reject and fix it myself" (flag rejectAndEditEnabled): the ONE verdict
  // PUT with editAfterReject:true, same toasts as a plain verdict. Resolves
  // with the rejected design; editAfterRejectApplied === false on it means
  // the API could not unlock the design for the editor (the popup then shows
  // its own toast instead of navigating). Navigation is the popup's job.
  function handleRejectAndEdit(design: Design, note: string) {
    async function transaction() {
      return await designRepository.rejectAndEdit(design, note);
    }

    return withToasts(
      transaction,
      t("designListStyle.updatingApproval"),
      t("designListStyle.verdictSuccessful"),
      t("designListStyle.approvalFailed"),
      setIsLoading
    );
  }

  function handleSkipAddToCart(design: Design, quantity: number) {
    if (cartHidden) {
      toast.error(t("designListStyle.cartNotAvailable"));
      return Promise.reject(new Error("Cart hidden"));
    }
    async function transaction() {
      // For Customers, we need to send for approval to Vendor first, then add to cart
      // For Admins/Vendors, we can truly skip approval and add to cart directly
      const shouldSkip = role === Role.Admin || role === Role.Vendor;

      return await designRepository
        .initiateApproval(design, {
          skip: shouldSkip,
          email: undefined,
        })
        .then((updatedDesign) => {
          handleAddToCart(updatedDesign, quantity);
          return updatedDesign;
        });
    }

    const loadingMessage =
      role === Role.Admin || role === Role.Vendor
        ? t("designListStyle.finishingDesign")
        : t("designListStyle.sendingApproval");
    const successMessage =
      role === Role.Admin || role === Role.Vendor
        ? t("designListStyle.designFinishedSuccessfully")
        : t("designListStyle.designSentForApproval");
    const errorMessage =
      role === Role.Admin || role === Role.Vendor
        ? t("designListStyle.approvalSkipFailed")
        : t("designListStyle.sendForApprovalFailed");

    return withToasts(
      transaction,
      loadingMessage,
      successMessage,
      errorMessage,
      setIsLoading
    );
  }

  // Flag REACT_APP_VENDOR_ORDER_WHILE_CUSTOMER_PENDING_ENABLED (Nichlas
  // 18/10, MUL352937): the dealer's row click on a design the customer still
  // has to approve. The popup's two calls, awaited in order: (1) POST
  // approval-start {skip:true} - the API settles the group under its own
  // flag (DesignVendorSettlePendingCustomerEnabled: the dealer owns the
  // design, its vote is Approved, the customer's is Pending) and commits to
  // NAV; (2) only when the answer is orderable by the one rule, today's
  // handleAddToCart (GetDesign, cart line, its toasts - the ONE success
  // toast of the chain, «Added to cart»; the settle step shows none). An
  // answer that is still not orderable gets the skip toast and STOPS -
  // nothing goes to the cart, no GetDesign, no success toast. That answer
  // is NOT harmless on the API side: without the API flag (or before the
  // API PR is deployed) the explicit skip still commits the design done
  // with the customer's Pending vote in place (FORM 3, which neither skip
  // nor order can repair) - exactly what the popup's SKIP does today for a
  // design with a customer. Hence the order of the flags: FE on only after
  // the API flag is on; FE off BEFORE the API flag is turned off. Errors
  // are rethrown after withToasts' one toast, as every other handler here;
  // the row catches them. A second call for a design whose chain is still
  // running is ignored (settlingDesignIds). The row gates on the FE flag;
  // this is the behaviour behind its button, never reached with the flag
  // off.
  async function handleSettleAndAddToCart(
    design: Design,
    quantity: number,
  ): Promise<void> {
    if (cartHidden) {
      toast.error(t("designListStyle.cartNotAvailable"));
      return;
    }
    if (settlingDesignIds.current.has(design.id)) return;
    settlingDesignIds.current.add(design.id);
    try {
      let orderable = false;
      const settle = async (): Promise<Design> => {
        const settled = await designRepository.initiateApproval(design, {
          skip: true,
          email: undefined,
        });
        orderable = isDesignOrderable(settled);
        if (!orderable) {
          toast.error(t(DESIGNS_SKIPPED_NOT_APPROVED_KEY, { count: 1 }));
        }
        return settled;
      };

      const settled = await withToasts(
        settle,
        t("designListStyle.finishingDesign"),
        t("designListStyle.designFinishedSuccessfully"),
        t("designListStyle.approvalSkipFailed"),
        setIsLoading,
        { showSuccess: () => false },
      );
      if (!orderable) return;
      await handleAddToCart(settled, quantity);
    } finally {
      settlingDesignIds.current.delete(design.id);
    }
  }

  return {
    isLoading,
    handleCopyDesign,
    handleCreateDesign,
    handleGoToCreateDesign,
    handleChangeTitle,
    handleUpdateDesign,
    handleDeleteDesign,
    handleGoToUpdateDesign,
    handleAddToCart,
    handleAddItemsToCart,
    handleGoToDesignList,
    handleInitiateApproval,
    handlePlaceVerdict,
    handleDeleteLogo,
    handleUpdateThenCommit,
    handleCommit,
    handleApprovalSkip,
    handleSendApproval,
    handleSendApprovalWithEndCustomerPending,
    handleChangeStyle,
    handleSkipAddToCart,
    handleSettleAndAddToCart,
    handleRejectAndDelete,
    handleRejectAndEdit,
  };
}
